import { ArtistPointsResponse } from "./returnTypes";

export interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: number;
  environment: string;
  database: {
    status: string;
    database: string;
    responseTime: number;
    error?: string;
  };
}

export interface DbHealthStatus {
  status: string;
  database: string;
  responseTime: number;
  error?: string;
  timestamp: string;
}

export type Draft = {
  draftId: string;
  startDate: string;
  endDate: string;
};

export type ActiveDraft =
  | {
      active: true;
      draftId: string;
      startDate: string;
      endDate: string;
      currentDay: string;
      currentWeek: number;
      nextDeadline: string;
      areLineupsPending: boolean;
      leaderboardGroupId: string | null;
      nextDraft: Draft | null;
      prevDraft: Draft | null;
    }
  | {
      active: false;
      message: string;
      nextDraft: Draft | null;
      prevDraft: Draft | null;
    };

export interface ArtistSearchResult {
  artistId: string;
  artistName: string;
  imageUrl: string;
  countryCode: string;
  genres: string;
  gender: string;
  careerStage: string;
  growthLevel: string;
  currentPrice?: number | null;
  previousPrice?: number | null;
}

export interface Artist {
  id: string;
  name: string;
  imageUrl: string;
}

export interface ArtistPrice {
  currentPrice: number;
  previousPrice: number | null;
  highestPrice: number;
  lowestPrice: number;
}

export interface ArtistPoints {
  streamingPoints: number;
  socialPoints: number;
  showPoints: number;
  totalPoints: number;
}

export interface PlayerStats {
  streamingScore: number;
  socialScore: number;
  showScore: number;
  totalScore: number;
  ranks: {
    groupId: string;
    groupName: string;
    rank: number;
  }[];
}

export interface PlayerLineup {
  lineupIndex: number;
  lineupName: string;
  totalScore: number;
  currentBudget: number;
  freeTradesRemaining: number;
}

export interface CurrentLineup {
  name: string;
  totalScore: number;
  budget: number;
  freeTrades: number;
  artists: Artist[];
  boostedArtistId: string | null;
}

export interface NextWeekLineup {
  effectiveDate: string;
  artists: Artist[];
  boostedArtistId: string | null;
}

export interface EnrichedArtist extends Artist {
  artistPrice: ArtistPrice;
  artistPoints: ArtistPointsResponse;
}

export interface EnrichedCurrentArtist extends EnrichedArtist {
  artistRecruitmentPrice: number;
}

export declare type ApiArtist = EnrichedArtist | EnrichedCurrentArtist;

export interface EnrichedCurrentLineup extends Omit<CurrentLineup, "artists"> {
  artists: EnrichedCurrentArtist[];
}

export interface EnrichedNextWeekLineup extends Omit<
  NextWeekLineup,
  "artists"
> {
  artists: EnrichedArtist[];
}

export interface LineupHistoryArtist extends Artist {
  streamingPoints: number;
  socialPoints: number;
  showPoints: number;
  eventPoints: number;
  wasBoosted: boolean;
}

export interface LineupHistory {
  date: string;
  artists: LineupHistoryArtist[];
}

export interface Transaction {
  transactionDate: string;
  transactionType: string;
  artistId: string;
  artistName: string;
  price: number;
  amountPaid: number;
  amountRefunded: number;
  usedFreeTrade: boolean;
  effectiveDate: string;
  appliedAt: string;
}

export interface Group {
  groupId: string;
  name: string;
  isPrivate: boolean;
  maxMembers: number;
  isMember: boolean;
  joinCode?: string;
}

export interface GroupSearchResult {
  groupId: string;
  groupName: string;
  hostPlayerId: string;
  hostUsername: string;
  requiresJoinCode: boolean;
  memberCount: number;
  maxMembers: number;
}

export type SearchResult = ArtistSearchResult | GroupSearchResult;

export function isSearchResultArtist(
  res: SearchResult,
): res is ArtistSearchResult {
  if (typeof res === "string") return false;
  return "careerStage" in res;
}

export function isSearchResultGroup(
  res: SearchResult,
): res is GroupSearchResult {
  if (typeof res === "string") return false;
  return "groupId" in res;
}

export interface LeaderboardEntry {
  rank: number;
  playerId: string;
  username: string;
  score: number;
}

interface NewsItemBase {
  newsId: string;
  title: string;
  content: string | null;
  imageUrl: string | null;
  sponsored: boolean | null;
  sponsoredBy: string | null;
}

export interface PlayerNewsItem extends NewsItemBase {
  relatedArtistId: string | null;
  relatedGroupId: string | null;
  deliveredAt: string;
  isRead: boolean;
}

export interface GroupOrArtistNewsItem extends NewsItemBase {
  createdAt: string;
}
